/**
 * The asset moving into and out of NEAR Intents, block by block
 * (blackbeard/observer#39, item 4 as replaced).
 *
 * intents.near's own balance of the token, read at every NEAR block on a node
 * of ours that tracks the token's shard. A rise is the asset deposited, a fall
 * the asset withdrawn. Trades never show here — they move balances between
 * accounts inside intents.near and leave its total alone — so this is the
 * money arriving and leaving, exactly, and nothing about who.
 */

import { useEffect, useState } from 'react'

import type { IntentsFlows } from '../api/generated/IntentsFlows'
import { RequestFailed, fetchIntentsFlows } from '../api/rest'
import { height as fmtHeight, tokens, utc, when } from '../lib/format'
import { Field } from './Field'

const SPANS: [number, string][] = [
  [6, '6 h'],
  [24, '24 h'],
  [24 * 7, '7 d'],
]

const REFRESH_MS = 60_000

/** Changes listed before "older" is needed. */
const SHOWN = 50

export function IntentsFlowPanel({ chain, symbol }: { chain: string; symbol: string }) {
  const [hours, setHours] = useState(24)
  const [data, setData] = useState<IntentsFlows | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [all, setAll] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    const load = () =>
      fetchIntentsFlows(chain, hours, controller.signal)
        .then((d) => {
          setData(d)
          setError(null)
        })
        .catch((e: unknown) => {
          if (controller.signal.aborted) return
          setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
        })
    load()
    const id = window.setInterval(load, REFRESH_MS)
    return () => {
      window.clearInterval(id)
      controller.abort()
    }
  }, [chain, hours])

  const asset = data?.asset_symbol ?? symbol
  const d = data?.asset_decimals ?? 0
  const amount = (raw: string | null | undefined) => (raw ? `${tokens(raw, d)} ${asset}` : '—')
  const signed = (raw: string) =>
    raw.startsWith('-') ? `−${tokens(raw.slice(1), d)}` : `+${tokens(raw, d)}`
  const read =
    data?.read_from !== null && data?.read_from !== undefined && data.read_to !== null
      ? `NEAR blocks ${fmtHeight(data.read_from)}–${fmtHeight(data.read_to)}`
      : 'nothing read yet'
  const net = data && BigInt(data.deposited) - BigInt(data.withdrawn)
  const changes = data ? (all ? data.changes : data.changes.slice(0, SHOWN)) : []

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <h2 className="panel-title">Into and out of NEAR Intents</h2>
        <span className="eyebrow">{read}</span>
      </div>
      <div className="pager" style={{ justifyContent: 'flex-start' }}>
        {SPANS.map(([h, label]) => (
          <button
            key={h}
            className="segmented panel-button"
            aria-pressed={hours === h}
            onClick={() => setHours(h)}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <p className="empty">{error}</p>}
      {!error && !data && <p className="empty">Reading…</p>}

      {data && (
        <div className="node-grid">
          <Field
            label="Deposited"
            value={amount(data.deposited)}
            note={`${data.deposits} block${data.deposits === 1 ? '' : 's'} in the last ${hours} h`}
          />
          <Field
            label="Withdrawn"
            value={amount(data.withdrawn)}
            note={`${data.withdrawals} block${data.withdrawals === 1 ? '' : 's'}`}
          />
          <Field
            label="Net"
            value={net === null || net === undefined ? '—' : `${signed(net.toString())} ${asset}`}
            note="into NEAR Intents, over the span"
          />
          <Field
            label="Held now"
            value={amount(data.balance_now)}
            note={
              data.balance_then ? `from ${amount(data.balance_then)}` : 'intents.near’s balance'
            }
          />
        </div>
      )}

      {data && data.changes.length > 0 && (
        <div className="scroll-x">
          <table className="board">
            <caption className="visually-hidden">
              Each block where intents.near’s {asset} balance changed, newest first
            </caption>
            <thead>
              <tr>
                <th scope="col" className="left">
                  When
                </th>
                <th scope="col">NEAR block</th>
                <th scope="col">Change</th>
                <th scope="col">Held after</th>
              </tr>
            </thead>
            <tbody>
              {changes.map((c) => (
                <tr key={c.near_height}>
                  <td className="left numeral" title={utc(c.at)}>
                    {when(c.at)}
                  </td>
                  <td className="numeral">{fmtHeight(c.near_height)}</td>
                  <td className="numeral">
                    {signed(c.delta)} {asset}
                  </td>
                  <td className="numeral">{tokens(c.balance, d)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.changes.length > SHOWN && !all && (
        <div className="pager">
          <button className="segmented panel-button" onClick={() => setAll(true)}>
            All {data.changes.length} →
          </button>
        </div>
      )}
      {data && data.changes.length === 0 && (
        <p className="empty">No {asset} moved into or out of NEAR Intents in the blocks read.</p>
      )}

      <p className="panel-note">
        intents.near&apos;s own {asset} balance, read at every NEAR block on a node of ours that
        tracks the token&apos;s shard. A rise is {asset} deposited into NEAR Intents, a fall {asset}{' '}
        withdrawn. This is exact for the money arriving and leaving, and says nothing about who
        moved it; a deposit and a withdrawal in the same block net to one change. Trades never
        appear: they move {asset} between accounts inside NEAR Intents and leave this total
        unchanged. Nothing before the first block read is known.
      </p>
    </section>
  )
}
